import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  BoxProps,
  Button,
  Container,
  Divider,
  MenuItem,
  Select,
  Stack,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';

import {
  IQuiz,
  // getQuizState,
  resetQuiz,
} from '../../../store/QuizSlice';
import {
  getDiagnosticBySubTopicId,
  getDiagnosticState,
  resetDiagnostic,
} from '../../../store/DiagnosticSlice';
import {
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import {
  createNewSubmissionByQuizId,
  getQuizDataForAboutPage,
  getSubmissionState,
} from '../../../store/SubmissionSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { checkUserPermission, getUrlConvertedString } from '../../../helper';

type TPaperOption = {
  label: string;
  value: string;
  item: {
    paper: string;
    quiz: IQuiz;
  };
};

const CustomAlert = ({ buttonData, alertText }) => {
  return (
    <Alert
      severity="info"
      icon={<InfoIcon sx={{ color: '#01736D' }} />}
      sx={{
        backgroundColor: TP_COLORS.white,
        color: '#01736D',
        borderRadius: '25px',
        marginBottom: '15px',
      }}
      action={
        <Button
          size="small"
          color="primary"
          variant="contained"
          onClick={buttonData.onClick}
          sx={{
            borderRadius: '30px',
            backgroundColor: '#01736D',
            color: TP_COLORS.white,
          }}
        >
          {buttonData.text}
        </Button>
      }
    >
      {alertText}
    </Alert>
  );
};

const containerStyles: SxProps = {
  background: 'linear-gradient(168.55deg, #006C9C -5.22%, #002536 98.06%)',
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const aboutQuizTypoProps: TypographyProps['sx'] = {
  component: 'h1',
  color: TP_COLORS.white,
  fontWeight: 600,
  fontSize: '32px',
  textAlign: 'center',
  fontFamily: 'inherit',
  marginBottom: '10px',
};

const aboutQuizParaTypoProps: TypographyProps['sx'] = {
  component: 'p',
  color: '#CBD5E1',
  fontWeight: 400,
  fontSize: '16px',
  textAlign: 'center',
  fontFamily: 'inherit',
  marginBottom: '10px',
};

const sectionTwo: BoxProps['sx'] = {
  position: 'relative',
  width: 'fit-content',
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: { xs: '10px', md: '-30px' },
};

const quizDetailsBox: BoxProps['sx'] = {
  padding: '10px 16px',
  bgcolor: '#FFFFFF1A',
  color: '#CBD5E1',
  borderRadius: '8px',
};

const outerButtonBox: BoxProps['sx'] = {
  transform: 'translateX(-50%)',
  backgroundColor: TP_COLORS.white,
  borderRadius: '30px',
  border: '1px solid',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  zIndex: 1000,
  width: '300px',
  height: '50px',
};

const totalAttemptsBox: BoxProps['sx'] = {
  backgroundColor: '#1E293B',
  border: '1px solid #F8FAFC',
  borderRadius: '30px',
  padding: '0px 0px 0px 20px',
  display: 'flex',
  alignItems: 'center',
  maxHeight: '80px',
  height: '40px',
  overflow: 'visible',
};

const buttonStyles: SxProps = {
  borderRadius: 0,
  fontWeight: 600,
  paddingY: '10px',
  paddingX: '20px',
  textTransform: 'none',
  color: 'black',
  backgroundColor: 'transparent',
};

const paperStyles: SxProps = {
  fontSize: '1rem',
  fontWeight: 600,
  textAlign: 'center',
  borderRight: '1px solid',
  borderRadius: 0,
  minWidth: '150px',
};

const expandStyles: SxProps = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  color: '#CBD5E1',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 'bold',
  textDecoration: 'underline',
};

const backButtonStyles: SxProps = {
  backgroundColor: '#1E293B33',
  color: TP_COLORS.white,
  borderRadius: '30px',
  border: '1px solid #1E293B33',
  padding: '5px 20px',
  '&:hover': {
    backgroundColor: '#1E293B',
    border: '1px solid #F8FAFC',
  },
};

const quizHistoryButtonStyles: SxProps = {
  color: TP_COLORS.white,
  fontWeight: 600,
  borderRadius: '30px',
  maxWidth: '150px',
  marginTop: '65px',
  fontSize: '18px',
  '&:hover': {
    textDecoration: 'underline',
  },
};

const dividerStyles: SxProps = {
  display: { xs: 'none', md: 'block' },
  borderStyle: 'dotted',
  borderColor: '#ccc',
  height: '80%',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1,
};

const aiTypoLeftProps: TypographyProps = {
  fontSize: { xs: '38px', md: '52px' },
  fontWeight: 800,
  fontFamily: 'inherit',
  sx: {
    background: 'linear-gradient(90deg, #006FD6 0%, #DB86F8 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const aiTypoRightProps: TypographyProps = {
  fontSize: { xs: '38px', md: '52px' },
  fontWeight: 800,
  fontFamily: 'inherit',
  sx: {
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const titleTypoProps: TypographyProps['sx'] = {
  fontSize: { xs: '38px', md: '52px' },
  fontWeight: 800,
  color: '#1E293B',
  fontFamily: 'inherit',
};

const bottomSection: BoxProps['sx'] = {
  background: `url(${process.env.PUBLIC_URL}/images/quiz/graphic.png)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  marginTop: '30px',
};

const AboutQuiz: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const submissionState = useAppSelector(getSubmissionState);
  // const quizState = useAppSelector(getQuizState);
  const authState = useAppSelector(getAuthState);
  const diagnosticState = useAppSelector(getDiagnosticState);

  // Permissions
  const createSubmissionPermission = checkUserPermission(authState.user, {
    submission: ['create'],
  });

  // State
  const [
    selectedPaperData,
    setSelectedPaperData,
  ] = useState<TPaperOption | null>(null);
  // const [redirect, setRedirect] = useState(false);
  const [showMore, setShowMore] = useState(false);

  let urlConvertedCurriculum = '';
  let urlConvertedSubject = '';
  let urlConvertedTopic = '';
  let urlConvertedSubTopic = '';

  const subTopicId = params.subtopicId;
  const subTopicData = subTopicState.subTopic;
  const quizData = submissionState.quizDataForAboutPage;
  const topicData =
    subTopicData && subTopicData.topic ? subTopicData.topic : undefined;

  const quizCount =
    subTopicData && subTopicData.quizCount ? subTopicData.quizCount : undefined;

  const isDiagnostic =
    (topicData && topicData.name === 'Diagnostic Challenge') || false;

  const diagnosticData = diagnosticState.diagnostic;

  const queryStringParams = new URLSearchParams(
    `?${location.hash.substring(1)}`
  );

  const urlPaper = queryStringParams.get('paper') || '';
  const paperOptions: Array<TPaperOption> = [];

  if (quizData && quizData.list && quizData.list.length) {
    quizData.list.forEach((item) => {
      paperOptions.push({
        item,
        label: item.paper,
        value: item.quiz && item.quiz._id ? item.quiz._id : '',
      });
    });
  }

  if (topicData) {
    urlConvertedCurriculum = getUrlConvertedString(topicData.curriculum);
    urlConvertedSubject = getUrlConvertedString(topicData.subject);
    urlConvertedTopic = getUrlConvertedString(topicData.name);
  }

  if (subTopicData) {
    urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);
  }

  useEffect(() => {
    dispatch(resetQuiz());

    return () => {
      dispatch(resetQuiz());
      dispatch(resetDiagnostic());
    };
  }, []);

  useEffect(() => {
    if (subTopicId) {
      dispatch(getSubTopicById(subTopicId));
      dispatch(getQuizDataForAboutPage({ subTopicId }));
    }
  }, [subTopicId]);

  useEffect(() => {
    if (quizData && quizData.list && quizData.list.length) {
      let selectedItem = quizData.list[0];

      if (urlPaper) {
        const matchItem = quizData.list.find((item) => item.paper === urlPaper);

        if (matchItem) {
          selectedItem = matchItem;
        }
      }

      setSelectedPaperData({
        label: selectedItem.paper,
        value:
          selectedItem.quiz && selectedItem.quiz._id
            ? selectedItem.quiz._id
            : '',
        item: selectedItem,
      });
    }
  }, [quizData]);

  useEffect(() => {
    if (
      subTopicId &&
      isDiagnostic &&
      diagnosticState.status !== 'loading' &&
      authState.user
    ) {
      dispatch(
        getDiagnosticBySubTopicId({ subTopicId, userId: authState.user._id })
      );
    }
  }, [isDiagnostic]);

  const handlePaperChange = (option: TPaperOption) => {
    setSelectedPaperData(option);
  };

  const startQuiz = () => {
    if (selectedPaperData && selectedPaperData.value && subTopicId) {
      dispatch(
        createNewSubmissionByQuizId({
          subTopicId,
          quizId: selectedPaperData.value,
        })
      );
    }
  };

  const getViewQuizHistoryUrl = () => {
    if (selectedPaperData) {
      return `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}/quiz-history/${selectedPaperData.value}`;
    }
    return '#';
  };

  const toggleShowMore = () => setShowMore((prev) => !prev);

  const getTotalAttempts = (subTopicName: string | undefined): number => {
    if (!subTopicName) return 0;

    const nameLength = subTopicName.length;
    const maxLength = 30;
    const normalized = Math.min(nameLength / maxLength, 1);

    const minAttempts = 500;
    const maxAttempts = 1200;
    const attempts = Math.floor(
      minAttempts + (maxAttempts - minAttempts) * normalized
    );

    return attempts;
  };

  const fullText = `This quiz will test your understanding of ${subTopicData?.name}, a key area in ${topicData?.name} from the ${topicData?.subject} syllabus under the ${topicData?.curriculum}. Practising with quizzes like this boosts your confidence and helps you prepare effectively for your exams.`;
  const charLimit = 140;
  const displayText = showMore
    ? fullText
    : `${fullText.substring(0, charLimit)}... `;

  const difficulty =
    selectedPaperData &&
    selectedPaperData.item &&
    selectedPaperData.item.quiz &&
    selectedPaperData.item.quiz.difficulty === 'ANY'
      ? 'Mixed Difficulty Level'
      : selectedPaperData?.item?.quiz?.difficulty || '-';

  return (
    <Box>
      <Box component="section" sx={containerStyles}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              paddingTop: '30px',
            }}
          >
            <Button
              size="large"
              variant="text"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{ ...backButtonStyles }}
            >
              Back
            </Button>

            <Box sx={totalAttemptsBox}>
              <Typography sx={{ color: '#F8FAFC' }}>
                Total Attempts: {getTotalAttempts(subTopicData?.name)}+
              </Typography>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/quiz/rocket.gif`}
                alt="rocket-graphic"
                sx={{
                  width: '60px',
                  height: 'auto',
                  transform: 'rotate(40deg)',
                }}
              />
            </Box>
          </Box>
          {diagnosticData && diagnosticData._id ? (
            <Stack alignItems="center">
              <CustomAlert
                alertText="Check your previous assessment report"
                buttonData={{
                  text: 'Click here',
                  onClick: () => {
                    navigate(
                      `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}/diagnostic-quiz/${diagnosticData._id}`
                    );
                  },
                }}
              />
            </Stack>
          ) : null}

          <Container maxWidth="md">
            <Box sx={sectionTwo}>
              <img
                src={`${process.env.PUBLIC_URL}/images/quiz/quiz-time.png`}
                alt="about-quiz"
                style={{ width: '350px', height: '230px' }}
              />
              <Typography {...aboutQuizTypoProps}>About This Quiz</Typography>
              <Typography {...aboutQuizParaTypoProps}>
                Get ready for your {topicData?.curriculum} {topicData?.subject}{' '}
                quiz on {subTopicData?.name}.
              </Typography>
              <Box sx={{ width: { xs: 'none', md: '620px' } }}>
                <Typography {...aboutQuizParaTypoProps}>
                  {displayText}
                  {!showMore && fullText.length > charLimit && (
                    <Button onClick={toggleShowMore} sx={{ ...expandStyles }}>
                      more
                    </Button>
                  )}
                  {showMore && (
                    <Button onClick={toggleShowMore} sx={{ ...expandStyles }}>
                      less
                    </Button>
                  )}
                </Typography>
              </Box>
              <Stack
                component="section"
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                gap="15px"
                marginTop="15px"
                marginBottom="30px"
              >
                <Box sx={quizDetailsBox}>
                  <Typography sx={{ fontSize: '16px' }}>
                    {selectedPaperData &&
                    selectedPaperData.item &&
                    selectedPaperData.item.quiz &&
                    selectedPaperData.item.quiz.noOfQuestions >= 0
                      ? `${selectedPaperData.item.quiz.noOfQuestions} Questions`
                      : '-'}
                  </Typography>
                </Box>
                <Box sx={quizDetailsBox}>
                  <Typography sx={{ fontSize: '16px' }}>
                    {difficulty}
                  </Typography>
                </Box>
              </Stack>
              <Typography sx={{ fontSize: '16px', color: '#F8FAFC' }}>
                Scroll to see all offerings
              </Typography>
              <img
                src={`${process.env.PUBLIC_URL}/images/quiz/scroll-graphic.gif`}
                alt="scroll-graphic"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <Button
                fullWidth
                variant="text"
                size="large"
                color="inherit"
                component={Link}
                to={getViewQuizHistoryUrl()}
                disabled={!selectedPaperData}
                sx={{ ...quizHistoryButtonStyles }}
              >
                Quiz History
              </Button>
            </Box>
          </Container>

          <Box position="fixed" bottom="9%" left="50%" sx={outerButtonBox}>
            <Box sx={{ flex: 1 }}>
              {paperOptions.length ? (
                <Select
                  size="medium"
                  variant="standard"
                  value={selectedPaperData?.label || ''}
                  disableUnderline
                  sx={{ ...paperStyles }}
                >
                  {paperOptions.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.label}
                      onClick={() => handlePaperChange(item)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Box px={2} py={1}>
                  -
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              {createSubmissionPermission && (
                <LoadingButton
                  variant="text"
                  size="large"
                  color="inherit"
                  onClick={startQuiz}
                  disabled={!selectedPaperData || !quizCount}
                  loading={submissionState.status === 'loading'}
                  sx={{ ...buttonStyles }}
                >
                  {quizCount ? 'Start a Quiz' : 'Quiz Upcoming'}
                </LoadingButton>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={bottomSection}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'stretch',
            position: 'relative',
            width: '100%',
          }}
        >
          <Divider orientation="vertical" flexItem sx={{ ...dividerStyles }} />

          <Box
            sx={{
              flex: 1,
              textAlign: { xs: 'center', md: 'right' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{ ...titleTypoProps, pr: { xs: 'none', md: '80px' } }}
              gutterBottom
            >
              <Typography {...aiTypoLeftProps}>
                AI Powered <br />
              </Typography>
              Marking Scheme
            </Typography>

            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/quiz/left-design.png`}
              alt="Left Image"
              sx={{
                width: { xs: '100%', md: '600px' },
                height: 'auto',
                marginTop: '20px',
                alignSelf: 'flex-start',
                left: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              textAlign: { xs: 'center', md: 'left' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              mt: { xs: '30px', md: 0 },
            }}
          >
            <Typography
              variant="h6"
              sx={{ ...titleTypoProps, pl: { xs: 'none', md: '80px' } }}
              gutterBottom
            >
              <Typography {...aiTypoRightProps}>
                AI Powered <br />
              </Typography>
              Detailed Report
            </Typography>

            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/quiz/right-design.png`}
              alt="Right Image"
              sx={{
                width: { xs: '100%', md: '600px' },
                height: 'auto',
                marginTop: { xs: '20px', md: '-30px' },
                alignSelf: 'flex-end',
                right: 0,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutQuiz;
